<template>
  <DialogModal
    :show="dialogData.isOpen"
    :title="dialogData.title"
    :description="dialogData.description"
    :action-buttons="[
      { onClick: dialogData.accept, type: 'primary', label: 'Create' },
      { onClick: dialogData.cancel, type: 'secondary', label: 'Cancel' },
    ]"
  >
    <FormKit type="form" id="newTemplateForm" @submit="handleCreateTemplate" :actions="false">
      <FormKit type="text" label="Name" name="name" validation="required" placeholder="Template Name" id="newTemplateInput" />
    </FormKit>
  </DialogModal>
  <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg p-8 mb-8 shadow-lg">
    <div class="max-w-3xl">
      <h2 class="text-4xl font-bold mb-4">Introducing AI Template Generator</h2>
      <p class="text-lg mb-6">
        Create professional templates instantly using the power of AI. Simply describe what you need, and let our AI do the heavy lifting.
      </p>

      <RouterLink
        :to="{ name: 'templateFromExample' }"
        class="bg-white text-indigo-600 px-6 py-3 rounded-md font-semibold hover:bg-indigo-50 transition-colors"
        >Try it now!</RouterLink
      >
    </div>
  </div>
  <div class="flex items-center justify-between">
    <h1 class="text-2xl font-bold grow">Templates</h1>
    <LSButton size="small" class="mr-4" type="primary" variant="filled" @click="router.push({ name: 'templateFromExample' })"
      >Create with AI</LSButton
    >
    <LSButton size="small" type="secondary" variant="filled" @click="dialogData.isOpen = true">Create Template</LSButton>
  </div>
  <div class="mt-8">
    <div class="flex items-center">
      <div
        @click="showArchived = false"
        class="cursor-pointer px-4 py-2 rounded-md border border-l-0 border-b-0 border-gray-300"
        :class="{ 'text-indigo-600 bg-white font-bold': !showArchived, 'text-gray-500 bg-gray-100': showArchived }"
      >
        Active
      </div>
      <div
        @click="showArchived = true"
        class="cursor-pointer px-4 py-2 rounded-md border border-l-0 border-b-0 border-gray-300"
        :class="{ 'text-indigo-600 bg-white font-bold': showArchived, 'text-gray-500 bg-gray-100': !showArchived }"
      >
        Archived
      </div>
    </div>
    <table class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-50">
        <tr>
          <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
          <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
          <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
          <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        <tr v-for="template in templates" :key="template.id" class="hover:bg-gray-50 transition-colors">
          <td class="px-6 py-4 whitespace-nowrap">
            <h3 class="font-semibold text-slate-800">{{ template.name }}</h3>
          </td>
          <td class="px-6 py-4">
            <p class="text-slate-600">{{ template.description }}</p>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <p class="text-slate-600">{{ $d(template.created_at, { dateStyle: 'short', timeStyle: 'short' }) }}</p>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-right">
            <template v-if="!template.archived">
              <button
                class="text-slate-900 rounded-lg px-4 py-2 hover:bg-primary-600 transition-colors"
                @click="router.push({ name: 'edit-template', params: { templateID: template.id } })"
                title="Edit Template"
              >
                <PencilIcon class="h-4 w-4" />
              </button>
              <button
                class="text-slate-900 rounded-lg px-4 py-2 hover:bg-primary-600 transition-colors"
                @click="archiveTemplateMutation.mutate(template.id!)"
                title="Archive Template"
              >
                <ArchiveBoxArrowDownIcon class="h-4 w-4" />
              </button>
            </template>
            <button
              v-else
              class="text-slate-900 rounded-lg px-4 py-2 hover:bg-primary-600 transition-colors"
              @click="unarchiveTemplateMutation.mutate(template.id!)"
              title="Unarchive Template"
            >
              <ArchiveBoxXMarkIcon class="h-4 w-4" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import LSButton from '@/components/LSButton.vue'
import { useRouter, useRoute } from 'vue-router'
import { archiveTemplate, createTemplate, getTemplates, unarchiveTemplate } from '@/admin/services/template-service'
import { errorNotification, successNotification } from '@/utils/notifications'
import type { AxiosError } from 'axios'
import DialogModal from '@/components/DialogModal.vue'
import { ref, watch } from 'vue'
import { getNode } from '@formkit/core'
import { PencilIcon, ArchiveBoxXMarkIcon, ArchiveBoxArrowDownIcon } from '@heroicons/vue/24/outline'

const router = useRouter()
const route = useRoute()

const showArchived = ref(route.query.archived === 'true')

watch(showArchived, () => {
  queryClient.invalidateQueries({ queryKey: ['templates'] })
  router.push({ name: 'templates', query: { archived: showArchived.value ? 'true' : 'false' } })
})

const { data: templates } = useQuery({
  queryKey: ['templates'],
  queryFn: async () => {
    console.log('showArchived', showArchived.value)
    const templates = await getTemplates(showArchived.value)
    return templates.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  },
})

const archiveTemplateMutation = useMutation({
  mutationFn: archiveTemplate,
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['templates'] })
    successNotification('Template archived')
  },
  onError: (error: Error) => {
    errorNotification((error as AxiosError)?.response?.data?.errors)
  },
})

const queryClient = useQueryClient()
const createTemplateMutation = useMutation({
  mutationFn: createTemplate,
  onSuccess: async () => await queryClient.invalidateQueries({ queryKey: ['templates'] }),
  onError: (error: Error) => {
    errorNotification((error as AxiosError)?.response?.data?.errors)
  },
})

const unarchiveTemplateMutation = useMutation({
  mutationFn: unarchiveTemplate,
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['templates'] })
    successNotification('Template unarchived')
  },
  onError: (error: Error) => {
    errorNotification((error as AxiosError)?.response?.data?.errors)
  },
})

const handleCreateTemplate = async ({ name }: { name: string }) => {
  await createTemplateMutation.mutateAsync({
    name,
    description: 'New Template',
    public: false,
    definition: { type: 'doc', content: [] },
    archived: false,
  })
  router.push({ name: 'edit-template', params: { templateID: createTemplateMutation.data?.value?.id } })
}

const dialogData = ref({
  isOpen: false,
  description: 'Create a new template',
  title: 'Create Template',
  accept: () => {
    const newTemplateNameNode = getNode('newTemplateInput')
    newTemplateNameNode?.submit()
  },
  cancel: () => {
    dialogData.value.isOpen = false
  },
})
</script>
